
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { AuthTab, AuthMode } from '@/context/AuthFormContext';
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from './authErrorHandler';

export const useSignupHandler = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Signup function with Supabase
  const handleSignup = async (
    e: React.FormEvent,
    email: string,
    password: string,
    setTab: (tab: AuthTab) => void,
    setMode: (mode: AuthMode) => void
  ) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Check if email already exists before signup attempt
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', email)
        .maybeSingle();
      
      if (existingUser) {
        throw new Error('Email already registered');
      }
      
      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            email: email
          }
        }
      });
      
      if (error) throw error;

      // Check if user already exists
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        throw new Error('The email address is already registered');
      }
      
      // Check if email confirmation is required
      const emailConfirmationRequired = data.session === null && data.user?.confirmed_at === null;
      
      if (emailConfirmationRequired) {
        // Switch to login tab
        setTab('login');
        setMode('login');
      } else {
        // Auto-login if email confirmation is not required
        // Redirect to home page
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      
      const errorMessage = getErrorMessage(error);
      
      toast({
        title: "Signup failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleSignup
  };
};
